import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CaseStudyCard from './components/CaseStudyCard';
import TestimonialGrid from './components/TestimonialGrid';
import ROICalculatorPanel from './components/ROICalculatorPanel';
import ComparisonChart from './components/ComparisonChart';
import IndustryBenchmarkPanel from './components/IndustryBenchmarkPanel';
import SocialProofPanel from './components/SocialProofPanel';

const CustomerSuccessStoriesROICalculator = () => {
  const [selectedDepartmentType, setSelectedDepartmentType] = useState('all');
  const [selectedOrgSize, setSelectedOrgSize] = useState('all');
  const [selectedUseCase, setSelectedUseCase] = useState('all');
  const [activeTab, setActiveTab] = useState('case-studies');
  const [showExportModal, setShowExportModal] = useState(false);

  const handleFilterChange = (filters) => {
    // Filter changes handled by GlobalFilterBar component
  };

  const tabs = [
    { id: 'case-studies', label: 'Case Studies', icon: 'FileText' },
    { id: 'testimonials', label: 'Testimonials', icon: 'MessageSquare' },
    { id: 'roi-calculator', label: 'ROI Calculator', icon: 'Calculator' },
    { id: 'benchmarks', label: 'Industry Benchmarks', icon: 'TrendingUp' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <Header />
      <GlobalFilterBar onFilterChange={handleFilterChange} />
      <ComplianceAlertBanner />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl p-8 mb-8 text-white">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <Icon name="Award" size={32} className="text-emerald-100" />
                <h1 className="text-3xl font-bold">Customer Success Stories & ROI Calculator</h1>
              </div>
              <p className="text-emerald-50 text-lg mb-6 max-w-3xl">
                Discover how PNG government departments and private sector organizations achieve measurable results with our HR compliance platform. Calculate your potential savings with our interactive ROI calculator.
              </p>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Icon name="Building2" size={20} className="text-emerald-200" />
                  <span className="text-sm font-medium">50+ PNG Organizations</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="TrendingUp" size={20} className="text-emerald-200" />
                  <span className="text-sm font-medium">Average 45% Cost Reduction</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Clock" size={20} className="text-emerald-200" />
                  <span className="text-sm font-medium">3-Month Payback Period</span>
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              className="bg-white text-emerald-600 hover:bg-emerald-50 border-white"
              iconName="Download"
              onClick={() => setShowExportModal(true)}
            >
              Export ROI Report
            </Button>
          </div>
        </div>

        {/* Social Proof Panel */}
        <SocialProofPanel />

        {/* Tabs Navigation */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 mb-6">
          <div className="flex items-center gap-2 p-2 overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400' :'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        {(activeTab === 'case-studies' || activeTab === 'testimonials') && (
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-4 mb-6">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Icon name="Filter" size={16} className="text-gray-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter by:</span>
              </div>
              <select
                value={selectedDepartmentType}
                onChange={(e) => setSelectedDepartmentType(e?.target?.value)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Department Types</option>
                <option value="government">Government</option>
                <option value="private">Private Sector</option>
                <option value="provincial">Provincial Administration</option>
              </select>
              <select
                value={selectedOrgSize}
                onChange={(e) => setSelectedOrgSize(e?.target?.value)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Organization Sizes</option>
                <option value="small">1-50 Employees</option>
                <option value="medium">51-500 Employees</option>
                <option value="large">500+ Employees</option>
              </select>
              <select
                value={selectedUseCase}
                onChange={(e) => setSelectedUseCase(e?.target?.value)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Use Cases</option>
                <option value="compliance">Compliance Automation</option>
                <option value="payroll">Payroll Processing</option>
                <option value="workforce">Workforce Management</option>
                <option value="document">Document Management</option>
              </select>
              {(selectedDepartmentType !== 'all' || selectedOrgSize !== 'all' || selectedUseCase !== 'all') && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => {
                    setSelectedDepartmentType('all');
                    setSelectedOrgSize('all');
                    setSelectedUseCase('all');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'case-studies' && (
          <CaseStudyCard
            departmentType={selectedDepartmentType}
            orgSize={selectedOrgSize}
            useCase={selectedUseCase}
          />
        )}

        {activeTab === 'testimonials' && (
          <TestimonialGrid
            departmentType={selectedDepartmentType}
            orgSize={selectedOrgSize}
            useCase={selectedUseCase}
          />
        )}

        {activeTab === 'roi-calculator' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ROICalculatorPanel onExport={() => setShowExportModal(true)} />
            <ComparisonChart />
          </div>
        )}

        {activeTab === 'benchmarks' && <IndustryBenchmarkPanel />}
      </main>
      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Export ROI Report</h3>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Generate a personalized ROI report and business case presentation for stakeholder review.
            </p>
            <div className="space-y-3">
              <Button
                variant="default"
                fullWidth
                iconName="FileText"
                onClick={() => {
                  alert('PDF Report exported successfully!');
                  setShowExportModal(false);
                }}
              >
                Export as PDF
              </Button>
              <Button
                variant="outline"
                fullWidth
                iconName="FileSpreadsheet"
                onClick={() => {
                  alert('Excel Report exported successfully!');
                  setShowExportModal(false);
                }}
              >
                Export as Excel
              </Button>
              <Button
                variant="outline"
                fullWidth
                iconName="Presentation"
                onClick={() => {
                  alert('PowerPoint Presentation exported successfully!');
                  setShowExportModal(false);
                }}
              >
                Export as PowerPoint
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSuccessStoriesROICalculator;